<i18n>
{
  "en": {
    "network": {
      "id": "ID",
      "rangeType": "Type",
      "broadcastAddress": "Broadcast",
      "netmaskAddress": "Netmask",
      "netmaskMask": "Mask",
      "startAddress": "IP From",
      "endAddress": "IP To",
      "startPort": "Port From",
      "endPort": "Port To",
      "agent": "Agent",
      "location": "Location",
      "title": "Network range",
      "title0": "Network ranges",
      "create": {
        "hint": "Add new agent manually",
        "newNetworkRange": "New network range"
      },
      "delete": {
        "confirm": "Do you really wish to remove the network range?"
      },
      "groups": {
        "networkRange": "Network range",
        "addresses": "Addresses"
      },
      "save": {
        "confirm": "Do you really wish to save network range?"
      },
      "largeRangeWarning": "Warning! The specified range exceeds 1024 addresses/ports. It is recommended to reduce the range to speed up device polling."
    }
  },
  "ru": {
    "network": {
      "id": "ID",
      "rangeType": "Тип",
      "broadcastAddress": "Широковещательный IP",
      "netmaskAddress": "Адрес",
      "netmaskMask": "Маска",
      "startAddress": "IP Начальный",
      "endAddress": "IP Конечный",
      "startPort": "Начальный порт",
      "endPort": "Конечный порт",
      "agent": "Агент",
      "location": "Локация",
      "title": "Сетевой диапазон",
      "title0": "Сетевые диапазоны",
      "create": {
        "hint": "Зарегистрировать нового агента вручную",
        "newNetworkRange": "Новый диапазон"
      },
      "delete": {
        "confirm": "Вы действительно хотите удалить сетевой диапазон?"
      },
      "groups": {
        "networkRange": "Сетевой диапазон",
        "addresses": "Адреса"
      },
      "save": {
        "confirm": "Вы действительно хотите сохранить информацию об сетевом диапазоне?"
      },
      "largeRangeWarning": "Внимание! Указанный диапазон превышает 1024 адреса/порта, рекомендуется уменьшить диапазон для ускорения опроса устройств."
    }
  }
}
</i18n>

<template>
  <div class="network">
    <div class="content-block dx-card responsive-paddings">
      <dx-form
        :key="id"
        label-location="left"
        :form-data="network"
        :col-count="1"
        :read-only="isFormDisabled"
        validation-group="networkValidation"
      >
        <dx-group-item
          :caption="$t('network.groups.networkRange')"
          :colCountByScreen="colCountByScreen"
        >
          <dx-simple-item data-field="rangeType"
            editor-type="dxSelectBox"
            :editor-options="editorSettings.rangeType"
          >
            <dx-label :text="$t('network.rangeType')" :show-colon="false" />
          </dx-simple-item>

          <dx-simple-item v-if="network && !network.isDisabledAgent"
            data-field="agent"
            editor-type="dxLookup"
            :editor-options="editorSettings.agent"
          >
            <dx-label :text="$t('network.agent')" :show-colon="false" />
            <dx-required-rule :message="$t('general.mandatory')" />
          </dx-simple-item>

          <dx-simple-item v-else
            data-field="agent"
            template="agent-disabled"
          >
            <dx-label :text="$t('network.agent')" :show-colon="false" />
            <dx-required-rule :message="$t('general.mandatory')" />
          </dx-simple-item>
          
          <dx-simple-item data-field="startPort" editor-type="dxNumberBox"
            :editor-options="editorsOptions.startPort"
          >
            <dx-label :text="$t('network.startPort')" :show-colon="false" />
            <dx-required-rule :message="$t('general.mandatory')" />
            <dx-validation-rule type="range" :min="1" :max="65535" :message="$t('general.badPort')" />
          </dx-simple-item>

          <dx-simple-item data-field="endPort" editor-type="dxNumberBox"
            :editor-options="editorsOptions.endPort"
          >
            <dx-label :text="$t('network.endPort')" :show-colon="false" />
            <dx-required-rule :message="$t('general.mandatory')" />
            <dx-validation-rule type="range" :min="1" :max="65535" :message="$t('general.badPort')" />
          </dx-simple-item>

          <dx-simple-item data-field="location.id" editor-type="dxLookup"
            :editor-options="editorsOptions.location"
          >
            <dx-label :text="$t('network.location')" :show-colon="false" />
          </dx-simple-item>

          <dx-simple-item data-field="community"
            :editor-options="editorSettings.communityName"
          >
            <dx-label text="Community name" :show-colon="false" />
            <dx-validation-rule type="stringLength" :max="32" />
            <dx-validation-rule type="pattern" :pattern="validationPatterns.communityName" />
          </dx-simple-item>
        </dx-group-item>

        <template #agent-disabled>
          <dx-text-box
            v-model="network.agent.url"
            :disabled="true"
          /> 
        </template>

        <dx-group-item
          :caption="$t('network.groups.addresses')"
          :colCountByScreen="colCountByScreen"
        >
          <dx-simple-item data-field="broadcastAddress"
            :visible="network && network.rangeType === 'BROADCAST'"
            :editor-options="editorsOptions.broadcastAddress"
          >
            <dx-label :text="$t('network.broadcastAddress')" :show-colon="false" />
            <dx-required-rule :message="$t('general.mandatory')" />
            <dx-validation-rule type="pattern" :pattern="validationPatterns.ip" :message="$t('general.badIpAddress')" />
          </dx-simple-item>

          <dx-simple-item data-field="netmaskAddress"
            :visible="network && network.rangeType === 'NETMASK'"
            :editor-options="editorsOptions.netmaskAddress"
          >
            <dx-label :text="$t('network.netmaskAddress')" :show-colon="false" />
            <dx-required-rule :message="$t('general.mandatory')" />
            <dx-validation-rule type="pattern" :pattern="validationPatterns.ip" :message="$t('general.badIpAddress')" />
          </dx-simple-item>

          <dx-simple-item data-field="netmaskMask"
            :visible="network && network.rangeType === 'NETMASK'"
            :editor-options="editorsOptions.netmaskMask"
          >
            <dx-label :text="$t('network.netmaskMask')" :show-colon="false" />
            <dx-required-rule :message="$t('general.mandatory')" />
            <dx-validation-rule type="custom" :validation-callback="netmaskValidation" :message="$t('general.badNetmask')" />
          </dx-simple-item>

          <dx-simple-item data-field="startAddress"
            :visible="network && network.rangeType === 'RANGE'"
            :editor-options="editorsOptions.startAddress"
          >
            <dx-label :text="$t('network.startAddress')" :show-colon="false" />
            <dx-required-rule :message="$t('general.mandatory')" />
            <dx-validation-rule type="pattern" :pattern="validationPatterns.ip" :message="$t('general.badIpAddress')" />
          </dx-simple-item>

          <dx-simple-item data-field="endAddress"
            :visible="network && network.rangeType === 'RANGE'"
            :editor-options="editorsOptions.endAddress"
          >
            <dx-label :text="$t('network.endAddress')" :show-colon="false" />
            <dx-required-rule :message="$t('general.mandatory')" />
            <dx-validation-rule type="pattern" :pattern="validationPatterns.ip" :message="$t('general.badIpAddress')" />
          </dx-simple-item>

          <dx-simple-item
            :visible="showLargeRangeWarning"
            :col-span="2"
            template="large-range-warning"
          />
        </dx-group-item>

        <template #large-range-warning>
          <div class="network-large-range-warning">
            {{ $t('network.largeRangeWarning') }}
          </div>
        </template>

        <dx-group-item>
          <!-- eslint-disable vue/no-unused-vars -->
          <div slot-scope="_" class="app-form-buttons">
            <aims2-button
              icon="save"
              type="default"
              :text="$t('general.save')"
              :disabled="isSaveDisabled"
              :busy="saving"
              @click="onSave"
            />

            <aims2-button
              icon="trash"
              type="danger"
              :text="$t('general.delete')"
              :disabled="isDeleteDisabled"
              :busy="deleting"
              @click="onDelete"
            />
          </div>
          <!-- eslint-enable -->
        </dx-group-item>
      </dx-form>

      <dx-load-panel :visible="loading" :position="{ of: '.network' }" />
    </div>
  </div>
</template>

<script>
import baseView from '../base.vue';
import appInfo from '../../app-info';

import {
  DxForm,
  DxSimpleItem,
  DxLabel,
  DxGroupItem,
  DxRequiredRule,
  DxValidationRule,
} from 'devextreme-vue/form';
import { DxTextBox } from 'devextreme-vue/text-box';
import CustomStore from 'devextreme/data/custom_store';
import ValidationEngine from 'devextreme/ui/validation_engine';
import { DxLoadPanel } from "devextreme-vue/load-panel";

import { notifier } from '@/shared/lib/notifier/index';

import aims2Button from '@/components/active-button.vue';
import showConfirmDialog from '@/components/confirm-dialog';
import { DataLocation } from "@/mixins/dataLocationMixin";
import { networkTypeDataSourceConfig } from './constants';

import {
  getNetworkRange,
  postNetworkRange,
  putNetworkRange,
  deleteNetworkRange,
  getAvailableAgentsPage,
  getAgent,
  getUserLocations
} from '@/services/api';

import { validationPatterns } from "@/utils/validation-patterns";

import { library } from '@fortawesome/fontawesome-svg-core';
import { faListOl } from '@fortawesome/free-solid-svg-icons';
library.add(faListOl);

export default {
  name: 'network',
  props: ['id'],
  extends: baseView,
  mixins: [DataLocation],
  components: {
    DxForm, DxSimpleItem, DxLabel, DxGroupItem, DxTextBox, DxRequiredRule, DxValidationRule, aims2Button, DxLoadPanel
  },
  data() {
    return {
      validationPatterns,
      network: {
        isDisabledAgent: false,
        agent: null,
        location: { id: null },
        rangeType: 'BROADCAST',
        broadcastAddress: '',
        netmaskAddress: '',
        netmaskMask: null,
        startAddress: '',
        endAddress: '',
        startPort: 161,
        endPort: 161,
        community: ''
      },
      loading: false,
      saving: false,
      deleting: false,
      colCountByScreen: {
        xs: 1,
        sm: 1,
        md: 2,
        lg: 2
      },
      networkTypeDataSourceConfig,
      editorSettings: {
        agent: {
          dataSource: {
            store: new CustomStore({
              key: 'id',                        
              load : async (options) => {
                let page = [
                  options.take || this.countRows() || 999,
                  options.take && options.skip? 1 + Math.round(options.skip / options.take): 1
                ];
                page = page.join(",");

                const sort = [];
                if (options.sort) {
                  options.sort.forEach(s => sort.push(s.desc? `${s.selector},DESC`: `${s.selector},ASC`));
                }

                const filterJson = options.searchValue ? [ 'url', 'contains', options.searchValue ] : undefined;
                const query = { page, sort, filterJson };

                const response = await getAvailableAgentsPage(query);

                return { totalCount : response.totalRows, data : response.pageRows };
              },
              // 
              // 
              // 
              byKey: async (key) => {
                if (this.network && this.network.isDisabledAgent)
                  return;
                else
                  return await getAgent(key);
              },
            })
          },
          key: 'id',
          displayExpr: 'url',
          searchEnabled: true,
          searchExpr: ['url'],
          searchMode: 'contains',
          showCancelButton: false,
          onValueChanged: this.agentChanged,
          dropDownOptions: { hideOnOutsideClick: true, showTitle: false }
        },
        rangeType: {
          dataSource: networkTypeDataSourceConfig,
          displayExpr: 'text',
          valueExpr: 'id'
        },
        communityName: { maxLength: 32 }
      }
    };
  },
  computed: {
    newtworkId() {
      return Number(this.$route.params.id);
    },
    isNew() {
      return this.newtworkId < 0;
    },
    editorsOptions() {
      const rangeWarningFieldAttr = {
        class: this.showLargeRangeWarning ? 'network-range-warning-field' : ''
      };

      return {
        broadcastAddress: { maxLength: 15 },
        netmaskAddress: { maxLength: 15, elementAttr: rangeWarningFieldAttr },
        netmaskMask: { elementAttr: rangeWarningFieldAttr },
        startAddress: { maxLength: 15, elementAttr: rangeWarningFieldAttr },
        endAddress: { maxLength: 15, elementAttr: rangeWarningFieldAttr },
        startPort: { elementAttr: rangeWarningFieldAttr },
        endPort: { elementAttr: rangeWarningFieldAttr },
        location: {
          dataSource: this.locations,
          key: 'id',
          displayExpr: 'name',
          valueExpr: 'id',
          searchEnabled: true,
          searchExpr: ['name'],
          searchMode: 'contains',
          showCancelButton: false,
          dropDownOptions: { hideOnOutsideClick: true, showTitle: false }
        }
      }
    },
    isFormDisabled() {
      return this.loading || this.saving || this.deleting;
    },
    isSaveDisabled() {
      return this.loading || this.saving || this.deleting;
    },
    isDeleteDisabled() {
      return this.loading || this.saving || this.deleting || this.isNew;
    },
    showLargeRangeWarning() {
      return this.getPollingRangeSize() > 1024;
    }
  },
  methods: {
    validate() {
      return ValidationEngine.validateGroup("networkValidation");
    },
    netmaskValidation({ value }) {
      if (typeof value === 'number') value = `${value}`;
      if (!value) return true;
      if (!value.match(/^\d+$/)) return false;
      const v = parseInt(value);
      return v >= 1 && v <= 32;
    },
    getPollingRangeSize() {
      if (!this.network || this.network.rangeType === 'BROADCAST') return 0;

      const addressCount = this.getAddressCount();
      const portCount = this.getPortCount();
      if (!addressCount || !portCount) return 0;

      return addressCount * portCount;
    },
    getAddressCount() {
      switch (this.network.rangeType) {
        case 'NETMASK':
          return this.getNetmaskAddressCount();
        case 'RANGE':
          return this.getIpRangeAddressCount();
        default:
          return 0;
      }
    },
    getNetmaskAddressCount() {
      if (this.ipToNumber(this.network.netmaskAddress) === null || !this.netmaskValidation({ value: this.network.netmaskMask })) return 0;

      return 2 ** (32 - parseInt(this.network.netmaskMask, 10));
    },
    getIpRangeAddressCount() {
      const startAddress = this.ipToNumber(this.network.startAddress);
      const endAddress = this.ipToNumber(this.network.endAddress);
      if (startAddress === null || endAddress === null) return 0;
      if (endAddress < startAddress) return 0;

      return endAddress - startAddress + 1;
    },
    getPortCount() {
      const startPort = Number(this.network.startPort);
      const endPort = Number(this.network.endPort);

      if (!Number.isInteger(startPort) || !Number.isInteger(endPort)) return 0;
      if (startPort < 1 || endPort > 65535 || endPort < startPort) return 0;

      return endPort - startPort + 1;
    },
    ipToNumber(ip) {
      if (typeof ip !== 'string' || !validationPatterns.ip.test(ip)) return null;

      return ip.split('.').reduce((sum, octet) => sum * 256 + Number(octet), 0);
    },
    updateBreadcrumb(title) {
      let breadcrumb = [{
        link: '/networks',
        title: this.$t('network.title0')
      }];

      if (!this.isNew) {
        let fullPathLocation = this.getFullPath(this.network.location.id);
        fullPathLocation = fullPathLocation.map((location) => {
          return {
            link: `/location/${location.id}`,
            title: location.name
          };
        });
        
        breadcrumb = breadcrumb.concat(fullPathLocation).concat({
          link: `/network/${this.newtworkId}`,
          title: title
        });
      } else {
        breadcrumb = breadcrumb.concat({
          link: '/network',
          title: this.$t('network.create.newNetworkRange')
        })
      }

      this.$store.commit('setBreadcrumb', breadcrumb);
    },
    async loadNetwork() {
      let title = '';
      try {
        this.loading = true;
        this.locations = await getUserLocations();

        if (!this.isNew) {
          this.network = await getNetworkRange(this.newtworkId);
          this.network.community = this.network.community || '';

          if (this.network.rangeType === 'BROADCAST' && this.network.startPort === this.network.endPort)
            title = `${this.network.broadcastAddress}:${this.network.startPort} (BROADCAST)`;
          if (this.network.rangeType === 'BROADCAST' && this.network.startPort !== this.network.endPort)
            title = `${this.network.broadcastAddress}:${this.network.startPort}-${this.network.endPort} (BROADCAST)`;
          if (this.network.rangeType === 'NETMASK')
            title = `${this.network.netmaskAddress}/${this.network.netmaskMask} (NETMASK)`;
          if (this.network.rangeType === 'RANGE' && this.network.startPort === this.network.endPort)
            title = `${this.network.startAddress}-${this.network.endAddress}:${this.network.startPort} (RANGE)`;
          if (this.network.rangeType === 'RANGE' && this.network.startPort !== this.network.endPort)
            title = `${this.network.startAddress}-${this.network.endAddress}:${this.network.startPort}-${this.network.endPort} (RANGE)`;
        } else {
          const location = this.locations.length > 0 ? this.locations[0] : null;
          this.network.location = location;
        }

        this.updateBreadcrumb(title);
      } catch(err) {
        this.onDataError(err);
      } finally {
        this.loading = false;
      }
    },
    async onSave() {
      const validate = this.validate();
      if (!validate.isValid) {
        validate.brokenRules[0].validator.focus();  
        return;
      }

      const response = await showConfirmDialog({
        title: this.$t('general.save'),
        message: this.$t('network.save.confirm'),
        btnType: 'default',
        okButton: this.$t('general.proceed'),
        cancelButton: this.$t('general.cancel'),
      });

      if (!response) return;

      try {
        this.saving = true;

        this.network.community = this.network.community || null;
        this.network.location = this.locations.find(location => location.id === this.network.location.id);

        switch (this.network.rangeType) {
          case 'BROADCAST':
            delete this.network.netmaskAddress;
            delete this.network.netmaskMask;
            delete this.network.startAddress;
            delete this.network.endAddress;
            break;
          case 'NETMASK':
            delete this.network.broadcastAddress;
            delete this.network.startAddress;
            delete this.network.endAddress;
            break;
          case 'RANGE':
            delete this.network.broadcastAddress;
            delete this.network.netmaskAddress;
            delete this.network.netmaskMask;
            break;
          default:
            break;
        }

        if (!this.isNew) {
          await putNetworkRange(this.newtworkId, this.network);
          notifier.showSuccess(this.$t('general.updateSuccess'));
        } else {
          await postNetworkRange(this.network);
          notifier.showSuccess(this.$t('general.createSuccess'));
        }

        this.$router.push({ name: "networks" });
      } catch(err) {
        this.onDataError(err);
      } finally {
        this.saving = false;
      }
    },
    async onDelete() {
      const response = await showConfirmDialog({
        title: this.$t('general.delete'),
        message: this.$t('network.delete.confirm'),
        btnType: 'default',
        okButton: this.$t('general.proceed'),
        cancelButton: this.$t('general.cancel'),
      });

      if (!response) return;

      try {
        this.deleting = true;
        await deleteNetworkRange(this.network.id);
        notifier.showSuccess(this.$t('general.deleteSuccess'));
        this.$router.go(-1);
      } catch(err) {
        this.onDataError(err);
      } finally {
        this.deleting = false;
      }
    },
    agentChanged(e) {
      if (e.previousValue !== null) {
        this.network.location = e.value.location;
      }
    },
  },
  async created() {
    document.title = `${appInfo.title} - ${this.$t('network.title')}`;

    this.loadNetwork();
  },
  watch: {
    id() {
      this.loadNetwork();
    }
  }
};
</script>

<style lang="scss">

.dx-item {
  margin-bottom: 10px;
}
.dx-invalid-message-content {
  padding: 0 10px !important;
}

.aim2-confirm-dialog-message {
  max-width: 480px;
}

.network {
  .network-large-range-warning {
    background-color: rgb(255, 252, 229);
    padding: 12px 40px;
    width: 100%;
  }

  .network-range-warning-field.dx-texteditor .dx-texteditor-input {
    background-color: rgb(255, 252, 229);
  }
}
</style>
